/**
 * M4S-5777 (X86) — a custom variable as a weighting target. Pure.
 *
 * Who is in each group of a variable is `custom-var-answers.ts`'s one rule (X57: the banner, a
 * filter and a weighting target read the same people). This module only adapts it for the
 * weighting engine: a target names a variable as `cv:<id>`, and the engine reads a target's answers
 * the way it reads a question's, upper-cased codes under one answer key. `withVariableAnswers` puts
 * each respondent's groups (upper-cased labels) under `customVarAnswerKey(id)` in a COPY of the
 * responses that only the weighting reads, and says which variables have a shown respondent in two
 * groups (their shares do not add up to 100%). Those keys never reach a row, a column or a file.
 */

import type { CustomVariable } from '@/app/(private)/ai/components/Crosstabs/crosstab-types';

import type { Answers } from './conditions';
import {
    customVarAnswerKey,
    hasVariableGroups,
    variableAnswerSets,
    variableGroupLabels,
} from './custom-var-answers';

/** How a weighting target names a custom variable: `cv:<id>` (case kept, as the variable's id). */
export const CV_TARGET_PREFIX = 'cv:';

/** Whether a weighting target's `variableId` names a custom variable. */
export function isVariableTarget(variableId: string): boolean {
    return variableId.startsWith(CV_TARGET_PREFIX);
}

/** The custom variable id a `cv:<id>` target names. */
export function variableIdOfTarget(variableId: string): string {
    return variableId.slice(CV_TARGET_PREFIX.length);
}

/** The answer key a variable's groups are read under (`custom-var-answers.ts`'s). */
export const cvAnswerKey = customVarAnswerKey;

/** Whether a variable has groups a respondent can be in, and at least two to weight between. */
export function hasVariableAnswers(cv: CustomVariable): boolean {
    return hasVariableGroups(cv) && variableGroupLabels(cv).length > 1;
}

/** A variable's groups, by label, in order (`variableGroupLabels`). */
export const variableAnswers = variableGroupLabels;

export interface VariableAnswers<R> {
    /** A shallow copy of each respondent, with a `CV:<ID>` cell per variable (upper-cased labels). */
    responses: R[];
    /** The variables some respondent in `among` is in two groups of. */
    overlapping: string[];
}

/**
 * Each respondent with an answer cell for every variable in `variables`, holding the upper-cased
 * labels of the groups they are in. The originals are not touched. A variable is `overlapping`
 * when a respondent in `among` (everyone when absent) is in more than one of its groups.
 */
export function withVariableAnswers<R extends Answers>(
    responses: readonly R[],
    variables: readonly CustomVariable[],
    among?: readonly number[],
): VariableAnswers<R> {
    const copies = responses.map((r) => ({ ...r }) as R);
    if (variables.length === 0) return { responses: copies, overlapping: [] };
    const overlapping = new Set<string>();
    const checked = among ? new Set(among) : null;
    for (const cv of variables) {
        if (!hasVariableGroups(cv)) continue;
        const key = cvAnswerKey(cv.id);
        variableAnswerSets(cv, responses).forEach((held, i) => {
            (copies[i] as Answers)[key] = new Set([...held].map((l) => l.trim().toUpperCase()));
            if (held.size > 1 && (!checked || checked.has(i))) overlapping.add(cv.id);
        });
    }
    return { responses: copies, overlapping: [...overlapping] };
}
